import { Check } from "lucide-react";
import Reveal from "./Reveal";
import { useT } from "../../i18n/I18nContext";

export const Pricing = () => {
  const t = useT();
  // Middle plan (index 1) is highlighted as "Most Popular"
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="py-24 lg:py-32 bg-[#F8FAFC] border-y border-slate-200/60"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#2563EB]">
              {t.pricing.eyebrow}
            </p>
          </Reveal>
          <Reveal delay={60}>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A2342] tracking-tight leading-[1.1]">
              {t.pricing.title}
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="mt-5 text-base lg:text-lg text-slate-600 leading-relaxed">
              {t.pricing.body}
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7 items-stretch">
          {t.pricing.plans.map((p, i) => {
            const isHighlight = i === 1;
            const slug = ["starter", "growth", "premium"][i] || `plan-${i}`;
            return (
              <Reveal key={p.name} delay={70 * i}>
                <div
                  data-testid={`pricing-card-${slug}`}
                  className={`relative h-full rounded-3xl p-8 lg:p-10 border transition-all duration-300 ${
                    isHighlight
                      ? "bg-[#0A2342] text-white border-[#0A2342] shadow-2xl shadow-[#0A2342]/25 lg:scale-[1.03]"
                      : "bg-white text-[#0A2342] border-slate-200/70 uv-card-hover"
                  }`}
                >
                  {isHighlight && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-[#10B981] px-3.5 py-1 text-[11px] font-bold uppercase tracking-wider text-white shadow-md">
                      {t.pricing.mostPopular}
                    </span>
                  )}
                  <h3 className={`text-2xl font-bold ${isHighlight ? "text-white" : "text-[#0A2342]"}`}>
                    {p.name}
                  </h3>
                  <p className={`mt-3 text-sm leading-relaxed ${isHighlight ? "text-slate-300" : "text-slate-600"}`}>
                    {p.tagline}
                  </p>

                  <div className={`mt-7 h-px ${isHighlight ? "bg-white/15" : "bg-slate-200"}`} />

                  <ul className="mt-7 space-y-3.5">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-3">
                        <span
                          className={`mt-0.5 inline-flex w-5 h-5 shrink-0 items-center justify-center rounded-full ${
                            isHighlight
                              ? "bg-[#10B981]/20 text-[#10B981]"
                              : "bg-[#10B981]/10 text-[#059669]"
                          }`}
                        >
                          <Check className="w-3 h-3" strokeWidth={3.5} />
                        </span>
                        <span className={`text-sm ${isHighlight ? "text-slate-200" : "text-slate-700"}`}>
                          {f}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <a
                    href="#contact"
                    data-testid={`pricing-cta-${slug}`}
                    className={`mt-9 inline-flex w-full items-center justify-center rounded-full px-6 py-3.5 text-sm font-semibold transition-all ${
                      isHighlight
                        ? "bg-[#10B981] text-white hover:bg-[#059669]"
                        : "bg-[#0A2342] text-white hover:bg-[#0A2342]/90"
                    }`}
                  >
                    {t.pricing.cta}
                  </a>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={300}>
          <p className="mt-10 text-center text-sm text-slate-500">
            {t.pricing.footnoteLead}{" "}
            <a
              href="#contact"
              className="font-semibold text-[#2563EB] hover:text-[#1D4ED8] transition-colors"
            >
              {t.pricing.footnoteLink}
            </a>{" "}
            {t.pricing.footnoteTail}
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default Pricing;
